import * as Inovx from '@shared';
import ViewerConstants from '../constants/ViewerConstants';
import View360Services from '../services/View360Services';
import MeasurementServices from '../services/MeasurementServices';
import ViewerActions from './ViewerActions';
import FacilityServices from '../services/FacilityServices';
import KnowledgeBookActions from './KnowledgeBookActions';
import TaxonomyActions from './TaxonomyActions';
import FacilityConstants from './../constants/FacilityConstants';
import FacilityStore from './../stores/FacilityStore';
import ViewerStore from './../stores/ViewerStore';
import {
    activateFacilitySlideoutPanelSession,
    closeFacilityAssetSetPanelSessions,
    closeFacilitySlideoutPanelSession,
    FACILITY_SLIDEOUT_PANEL_IDS,
} from '../helpers/facilitySlideoutPanelSession';
var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;
var FormHelper = Inovx.Shared.Common.FormHelper;
var PermissionService = Inovx.Shared.Permission.PermissionService;
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

var ORG_APP_NAME = "organization";
var ORG_MANAGE_PERMISSION = "Organization.Manage";

function _finishEdit(close: boolean) {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: FacilityConstants.FACILITY_FINISH_EDIT,
        close: close
    });
}

function _receiveAllFacilities(data: IFacility[]): void {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: FacilityConstants.FACILITY_GET_ALL,
        data: data
    });
}

function _receiveSearchResults(data: IFacility[]) {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: FacilityConstants.FACILITY_RECEIVE_SEARCH_RESULTS,
        data: data
    });
}

function _receiveFacilityDetails(facility: IFacilityDetail) {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: FacilityConstants.FACILITY_DETAILS,
        facility
    });
}

function _sendInvalidSave() {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: FacilityConstants.FACILITY_INVALID_ADD
    });
}

function _displayFacilityInaccessibleMessage() {
    let msgObj: IMessageObject;
    if (PermissionService.checkPermission(ORG_MANAGE_PERMISSION)) {
        const appUrl = UrlUtilities.getApplicationUrl()+'/'+ORG_APP_NAME;
        const link = `<a href="${appUrl}" target="_blank">${InovxLoader.t('facility:message.orgAccessRights')}</a>`
        let message: string = InovxLoader.t('facility:message.inaccessible1');
        message = message.replace("__hint__", link);
        msgObj = {
            message: message,
            messageHasTags: true
        }
    }
    else {
        msgObj = {
            message: InovxLoader.t('facility:message.inaccessible2')
        }
    }

    return new Promise<void>((resolve, reject) => {
        msgObj.confirmCallback = () => {
            resolve();
        }
        SystemActions.displayInfoMessage(msgObj);
    });
}

var FacilityActions = {
    async getAllFacilities() {
        const data = await FacilityServices.getAll();
        _receiveAllFacilities(data);
        return data;
    },
    async searchByName(name: string) {
        const data = await FacilityServices.searchByName(name);
        _receiveSearchResults(data);
        return data;
    },

    beginAdd(): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_BEGIN_ADD
        });
    },
    beginEdit(facility: IFacility): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_BEGIN_EDIT,
            facility: facility
        });
    },
    async openFacilityDetail(facilityID: string) {
        ViewerActions.hidePanoViewerImage();
        ViewerActions.setContentLayers({
            ...ViewerStore.getContentLayers(),
            positions360: true,
            scanPositions: true,
            colorScanPositions: true,
            blackAndWhiteScanPositions: true,
            imagePositions: true
        });

        if (!SystemStore.isActionInProgress(FacilityConstants.FACILITY_DETAILS)) {
            SystemActions.beginAction(FacilityConstants.FACILITY_DETAILS);

            try {
                let facilityDetail = await FacilityServices.getFacilityDetail(facilityID);
                _receiveFacilityDetails(facilityDetail);
                if (facilityDetail.KBTaxonomyID) {
                    await TaxonomyActions.getVisibleTaxonomyDetail(facilityDetail.KBTaxonomyID);
                }
                if (facilityDetail.AssetSetTaxonomyID) {
                    await TaxonomyActions.getTaxonomyDetail(facilityDetail.AssetSetTaxonomyID);
                }
                return facilityDetail;
            } finally {
                SystemActions.endAction(FacilityConstants.FACILITY_DETAILS);
            }
        }
    },
    async refreshSelectedFacilityDetail() {
        const selectedFacility = FacilityStore.getSelectedFacility();
        if (!selectedFacility?.ID) {
            return null;
        }

        const facilityDetail = await FacilityServices.getFacilityDetail(selectedFacility.ID);
        _receiveFacilityDetails(facilityDetail);
        ViewerStore.refreshFacilityViews(facilityDetail.Views);
        return facilityDetail;
    },
    async updateView360Metadata(update: IView360MetadataUpdate) {
        await View360Services.updateMetadata(update);
        return await this.refreshSelectedFacilityDetail();
    },
    async updateView360MetadataBatch(update: IView360BatchMetadataUpdate) {
        await View360Services.updateMetadataBatch(update);
        return await this.refreshSelectedFacilityDetail();
    },
    async deleteView360Many(items: IView360Identity[]) {
        await View360Services.deleteMany(items);
        return await this.refreshSelectedFacilityDetail();
    },
    editFacilityProperty(comp: string, key: string, value: any): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_UPDATE_EDIT,
            component: comp,
            key: key,
            value: value
        });
    },

    openAllFacilities() {
        _finishEdit(true);
        FacilityActions.resetFacilityDetails();
        FacilityActions.updateManageURL({});
    },

    openDataSources() {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_OPEN_DATA_SOURCES
        });
    },

    closeDataSources() {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_DATA_SOURCES
        });
    },

    async cancelEdit(): Promise<void> {
        const cancelCallback = async () => {
            if (FacilityStore.getIsEditing()) {
                let facility: IFacilityDetail = FacilityStore.getFacility();
                
                await FacilityActions.getFacilityAdapters(facility.ID);
                await FacilityActions.openFacilityDetail(facility.ID);

                facility = FacilityStore.getSelectedFacility();
                FacilityActions.beginEdit(facility);
            }
            else {
                FacilityActions.openAllFacilities();
            }
        }

        if (FacilityStore.getHasChanges()) {
            var callbackFunct = FacilityStore.getIsEditing() ? FacilityActions.editFacility : FacilityActions.createFacility;
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: callbackFunct,
                cancelCallback: async () => await cancelCallback()
            });
        } else {
            await cancelCallback();
        }
    },

    verifyFacilityModalChanges(): boolean {
        if (FacilityStore.getModalVisible()) {
            if (FacilityStore.getHasChanges()) {
                SystemActions.displayInfoMessage({
                    message: InovxLoader.t('facility:message.savePendingChangesInfo')
                });
                return false;
            }
        }
        return true;
    },
    verifyAndFinishFacilityModalChanges(close: boolean): boolean {
        var verified = true;
        if (FacilityStore.getModalVisible()) {
            verified = this.verifyFacilityModalChanges();
            if (verified) {
                _finishEdit(close);
            }
        }
        return verified;
    },

    resetFacilityDetails() {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_DETAILS_RESET
        });
    },
    async createFacility() {
        if (FacilityStore.getIsValidObject(FacilityConstants.FACILITY_ADD_FORM)) {
            if (!SystemStore.isActionInProgress(FacilityConstants.FACILITY_ADD)) {
                SystemActions.beginAction(FacilityConstants.FACILITY_ADD);

                try {
                    let facility: IFacilityDetail = FacilityStore.getFacility();
                    let data = FormHelper.trim(facility);
                    var form_data: any = new FormData();
                    form_data.append("ID", data.ID);
                    form_data.append("Name", data.Name);
                    form_data.append("Location", data.Location);
                    form_data.append("Synopsis", data.Synopsis);
                    form_data.append("SchemaID", data.SchemaID);
                    form_data.append(`Schemas[0]`, data.SchemaID);
                    form_data.append("KBTaxonomyID", data.KBTaxonomyID);
                    if (data?.ThumbnailData) {
                        form_data.append("ThumbnailData", data.ThumbnailData);
                    }

                    const AdapterConfigurations = FacilityStore.getFacilityAdapters();
                    if (AdapterConfigurations) {
                        AdapterConfigurations.filter((config: any) => config.FacilityID != null).forEach((config, index) => {                            
                            Object.entries(config).forEach(([obj, value]) => {
                                if (Array.isArray(value)) {
                                    value.forEach((arr, index2) => {
                                        Object.entries(arr)?.forEach(([obj2, value2]) => {
                                            form_data.append(`AdapterConfigurations[${index}][${obj}][${index2}][${obj2}]`, value2)
                                        })
                                    })
                                } else {
                                    Object.entries(value)?.forEach(([obj2, value2]) => {
                                        form_data.append(`AdapterConfigurations[${index}][${obj}][${obj2}]`, value2)
                                    })
                                }
                            })                            
                        });
                    }

                    await FacilityServices.createFacility(form_data);
                    _finishEdit(true);
                    await _displayFacilityInaccessibleMessage();
                    await FacilityActions.refreshData();
                    return facility;
                }
                finally {
                    SystemActions.endAction(FacilityConstants.FACILITY_ADD);
                }
            }
        }
        else {
            _sendInvalidSave();
        }
    },
    async editFacility() {
        if (FacilityStore.getIsValidObject(FacilityConstants.FACILITY_ADD_FORM)) {
            if (!SystemStore.isActionInProgress(FacilityConstants.FACILITY_EDIT)) {

                SystemActions.beginAction(FacilityConstants.FACILITY_EDIT);

                try {

                    let facility: IFacilityDetail = FacilityStore.getFacility();
                    let data = FormHelper.trim(facility);
                    var form_data: any = new FormData();
                    form_data.append("ID", data.ID);
                    form_data.append("Name", data.Name || "");
                    form_data.append("Location", data.Location || "");
                    form_data.append("Synopsis", data.Synopsis || "");
                    form_data.append("VersionToken", data.VersionToken);
                    form_data.append("SchemaID", data.SchemaID);
                    if (data.Schemas && data.Schemas.length > 0) {
                        data.Schemas.forEach((schema, index) => {
                            form_data.append(`Schemas[${index}]`, schema);
                        });
                    }
                    if (data.KBTaxonomyID) form_data.append("KBTaxonomyID", data.KBTaxonomyID);
                    if (data?.ThumbnailData) {
                        form_data.append("ThumbnailData", data.ThumbnailData);
                    }

                    const AdapterConfigurations = FacilityStore.getFacilityAdapters();
                    if (AdapterConfigurations) {
                        AdapterConfigurations.filter((config: any) => config.FacilityID != null).forEach((config, index) => {                           
                            Object.entries(config).forEach(([obj, value]) => {
                                if (Array.isArray(value)) {
                                    value.forEach((arr, index2) => {
                                        Object.entries(arr).forEach(([obj2, value2]) => {
                                            form_data.append(`AdapterConfigurations[${index}][${obj}][${index2}][${obj2}]`, value2)
                                        })
                                    })
                                } else {
                                    Object.entries(value).forEach(([obj2, value2]) => {
                                        form_data.append(`AdapterConfigurations[${index}][${obj}][${obj2}]`, value2)
                                    })
                                }
                            })                           
                        });
                    }
                    

                    await FacilityServices.editFacility(form_data);
                    await FacilityActions.refreshData();
                    const updatedFacility = await FacilityActions.openFacilityDetail(facility.ID);
                    facility.VersionToken = updatedFacility.VersionToken;
                    FacilityActions.beginEdit(facility);
                    _finishEdit(false);

                    return facility;
                }
                finally {
                    SystemActions.endAction(FacilityConstants.FACILITY_EDIT);
                }
            }
        } else {
            _sendInvalidSave();
        }
    },

    async deleteFacility(facility: IFacility) {
        if (!SystemStore.isActionInProgress(FacilityConstants.FACILITY_DELETE)) {
            SystemActions.beginAction(FacilityConstants.FACILITY_DELETE);

            try {
                await FacilityServices.deleteFacility(facility);
                await FacilityActions.refreshData();
            }
            finally {
                SystemActions.endAction(FacilityConstants.FACILITY_DELETE);
            }
        }
    },

    refreshData() {
        return FacilityActions.getAllFacilities();
    },

    updateManageURL(params: QueryParams) {
        const currentUrl = window.location.href.split('?')[0];
        const urlSearchParams = new URLSearchParams(window.location.search);
        const facilityId = urlSearchParams.get('facilityId') || '';
        let flag = false;

        if (params.facilityId !== undefined) {
            if (facilityId !== params.facilityId) {
                urlSearchParams.set('facilityId', params.facilityId);
                flag = true;
            }
        } else if (facilityId) {
            urlSearchParams.delete('facilityId');
            flag = true;
        }

        if (flag) {
            const newUrl = `${currentUrl}?${urlSearchParams.toString()}`;
            window.history.pushState({}, '', newUrl);
        }
        FacilityActions.updateContextHelp(params);
    },
    updateContextHelp(params: QueryParams) {
        //update help url
        let helpUrl = '../Help/en/FacilityExplorer.html';
        if (params.facilityId) {
            helpUrl = '../Help/en/FacilityPage.html';
        } else if (params.viewId || params.kvId) {
            helpUrl = '../Help/en/PanoramicImageViewer.html';
        }
        const anchorElement: any = document.getElementById('ihdr-help-for-page');
        if (anchorElement) {
            anchorElement.href = helpUrl;
        }
    },
    getParamsFromCurrentURL(): QueryParams {
        const params = new URLSearchParams(window.location.search);
        const queryParams: QueryParams = {};

        if (params.has('facilityId')) {
            queryParams.facilityId = params.get('facilityId')!;
        }
        if (params.has('viewId')) {
            queryParams.viewId = params.get('viewId')!;
        }
        if (params.has('kbId')) {
            queryParams.kbId = params.get('kbId')!;
        }
        if (params.has('kvId')) {
            queryParams.kvId = params.get('kvId')!;
        }
        if (params.has('assetSetId')) {
            queryParams.assetSetId = params.get('assetSetId')!;
        }
        if (params.has('ontologyId')) {
            queryParams.ontologyId = params.get('ontologyId')!;
        }
        if (params.has('rangeViewId')) {
            queryParams.rangeViewId = params.get('rangeViewId')!;
        }
        if (params.has('assetId')) {
            queryParams.assetId = params.get('assetId')!;
        }
        if (params.has('cltagId')) {
            queryParams.cltagId = params.get('cltagId')!;
        }
        if (params.has('mode')) {
            const mode = params.get('mode');
            queryParams.mode = mode === '3D' ? '3D' : mode === '3DRange' ? '3DRange' : mode === '3DSet' ? '3DSet' : mode === '360' ? '360' : mode === 'klib' ? 'klib' : mode === 'klibcard' ? 'klibcard' : mode === 'media' ? 'media' : 'exp';
        }

        return queryParams;
    },
    updateUrlWithExpanded(isExpanded: string = 'false') {
        const currentUrl = window.location.href;
        const urlObject = new URL(currentUrl);
        const searchParams = new URLSearchParams(urlObject.search);
        if (isExpanded === 'true') {
            searchParams.set('expand', isExpanded);
        } else {
            searchParams.delete('expand');
        }
        urlObject.search = searchParams.toString();
        window.history.replaceState(null, '', urlObject.toString());
        return urlObject.search;
    },
    openSidebar() {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_OPEN_SIDEBAR
        });
    },
    closeSidebar() {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_SIDEBAR
        });
    },
    openSearchbar() {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_OPEN_SEARCHBAR
        });
    },
    closeSearchbar() {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_SEARCHBAR
        });
    },
    openAssetSearchbar() {
        activateFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.assetTags);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_OPEN_ASSET_SEARCHBAR
        });
    },
    closeAssetSearchbar() {
        closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.assetTags);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_ASSET_SEARCHBAR
        });
    },
    async openLODViewer(ontologyID: string, contentTypes: string[], distanceThreshold? : number, rangePosition?: IVector3D, 
        overrideFocalDistance?:number, resetCamera: boolean = true, rangeView: IView360Detail = null) {

        let treeDetail = await FacilityServices.getOntologyTrees(ontologyID, contentTypes);

        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: ViewerConstants.VIEWER_SHOW_LOD,
            data: treeDetail,
            distanceThreshold: distanceThreshold,
            rangePosition: rangePosition,
            overrideFocalDistance: overrideFocalDistance,
            resetCamera: resetCamera,
            rangeView: rangeView
        });
    },
    async openKnowledgeViewInViewer(kv: IKnowledgeView) {
        let lodData: string | undefined = undefined;
        if (kv.Type === 2) { // 3D
            const is3DViewerVisible = ViewerStore.is3DViewerVisible();
            const selectedOntology = FacilityStore.getSelectedOntology();
            if (!is3DViewerVisible || (selectedOntology?.ID !== kv.OntologyID)) {
                lodData = await FacilityServices.getOntologyTrees(kv.OntologyID, ["Point Cloud Data", "CAD Data", "Mesh Data"]);
            }
        }
        await ViewerActions.openKnowledgeView(kv.ID, lodData);
    },
    switchToPanoViewerMode() {
        ViewerActions.setOpenPointCloud(false, true);
        FacilityActions.closeAssetSearchbar();
    },
    selectTab(tab='') {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_SELECTED_TAB,
            tab
        });
    },
    toggleAssetTagBar(status: boolean, resetDisplayMode: boolean = true) {
        if (resetDisplayMode) ViewerActions.resetDisplayMode();
        if (status) {
            activateFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.assetTags);
        } else {
            closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.assetTags);
        }
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.TOGGLE_ASSET_TAGBAR,
            status
        });
    },
    openKBbar() {
        closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeViewEdit);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_KNOWLEDGE_VIEW_EDIT_BAR
        });
        closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_ASSET_SETS_BAR
        });
        activateFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_OPEN_KNOWLEDGE_BOOK_BAR
        });
    },
    openKnowledgeViewEditBar() {
        closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_KNOWLEDGE_BOOK_BAR
        });
        activateFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeViewEdit);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_OPEN_KNOWLEDGE_VIEW_EDIT_BAR
        });
    },
    openFacilityAssetSetsBar() {
        if (ViewerStore.getSelectedKnowledgeView()?.ID) {
            ViewerActions.closeKnowledgeView(true);
        }
        closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_KNOWLEDGE_BOOK_BAR
        });
        activateFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_OPEN_ASSET_SETS_BAR
        });
    },
    openAssetSetVisualizationBar() {
        closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets);
        activateFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.assetSetVisualization);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_OPEN_ASSET_SET_VISUALIZATION_BAR
        });
    },
    toggleMeasurementBar(status: boolean) {
        if (status) {
            activateFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.measurements);
        } else {
            closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.measurements);
        }
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.TOGGLE_MEASUREMENTBAR,
            status
        });
    },
    toggleAssemblyLibraryBar(status: boolean) {
        if (status) {
            activateFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.assemblyLibrary);
        } else {
            closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.assemblyLibrary);
        }
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.TOGGLE_ASSEMBLYLIBRARYBAR,
            status
        });
    },
    closeKBbar() {
        closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_KNOWLEDGE_BOOK_BAR
        });
    },
    closeKnowledgeViewEditBar() {
        closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeViewEdit);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_KNOWLEDGE_VIEW_EDIT_BAR
        });
    },
    closeFacilityAssetSetsBar() {
        closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_ASSET_SETS_BAR
        });
        if (ViewerStore.getSavedKnowledgeView()) {
            void ViewerActions.changeKnowledgeBookViewingMode('manageKV');
        }
    },
    closeAssetSetVisualizationBar() {
        closeFacilitySlideoutPanelSession(FACILITY_SLIDEOUT_PANEL_IDS.assetSetVisualization);
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_ASSET_SET_VISUALIZATION_BAR
        });
    },
    async getView360Data(id: string) {
        let details = await View360Services.getView360(id);
        return details;
    },
    async getView360Measurements(id: string) {
        let measurements = await MeasurementServices.getMeasurementsByView360(id);
        return measurements;
    },
    async openKnowledgeLibrary() {
        const selectedFacility = FacilityStore.getSelectedFacility();

        const getFunctions: Promise<any>[] = [
            KnowledgeBookActions.getFacilityKnowledgeBooks(selectedFacility.ID)
        ];
        //if (selectedFacility.TaxonomyID) {
        //    getFunctions.push(TaxonomyActions.getTaxonomyDetail(selectedFacility.TaxonomyID));
        //}
        await Promise.all(getFunctions);

        closeFacilityAssetSetPanelSessions();
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_OPEN_KNOWLEDGE_LIBRARY
        });
    },
    closeKnowledgeLibrary() {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_KNOWLEDGE_LIBRARY
        });
    },
    openMediaGallery() {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_OPEN_MEDIA_GALLERY
        });
    },
    closeMediaGallery() {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_CLOSE_MEDIA_GALLERY
        });
    },
    setMediaGalleryViewMode(viewMode: MediaGalleryViewMode) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_SET_MEDIA_GALLERY_VIEW_MODE,
            viewMode
        });
    },
    setKnowledgeLibraryCardUrlState(state: Partial<KnowledgeLibraryCardUrlState>) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_SET_KNOWLEDGE_LIBRARY_CARD_URL_STATE,
            state
        });
    },
    async getFacilityAdapters(id: string) {
        if (!SystemStore.isActionInProgress(FacilityConstants.FACILITY_ADAPTERS)) {
            SystemActions.beginAction(FacilityConstants.FACILITY_ADAPTERS);

            try {
                const data = await FacilityServices.getAdapters(id);
                const params = { data, isUpdate: false }
                Inovx.Shared.Flux.AppDispatcher.dispatch({
                    actionType: FacilityConstants.FACILITY_ADAPTERS,
                    params
                });
            }
            finally {
                SystemActions.endAction(FacilityConstants.FACILITY_ADAPTERS);
            }
        }
    },
    synchronizeAdapterData(facility: IFacility, adapterType: number) {
        if (!SystemStore.isActionInProgress(FacilityConstants.FACILITY_SYNC_ADAPTER_DATA)) {
            SystemActions.beginAction(FacilityConstants.FACILITY_SYNC_ADAPTER_DATA);

            try {
                FacilityServices.synchronizeAdapterData(facility, adapterType);
            }
            finally {
                SystemActions.endAction(FacilityConstants.FACILITY_SYNC_ADAPTER_DATA);
            }
        }
    },
    updateFacilityAdapters(data: any, isUpdate: boolean = false) {
        const params = {data, isUpdate}
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.FACILITY_ADAPTERS,
            params
        });
    },
    toggleXMLEditor(flag: boolean) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.TOGGLE_XML_EDITOR,
            flag
        });
    },
    setXMLEditorData(data: IFacilityAdapterConfiguration) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: FacilityConstants.SET_XML_EDITOR_DATA,
            data
        });
    }
   
}

export default FacilityActions;
